import { useState, useEffect, useCallback } from "react";
import { Radio, Plus, Trash2, X, RefreshCw, Copy, Check, Signal, Tv, ExternalLink, AlertCircle } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const FORMAT_OPTIONS = ["mp3", "ogg", "opus", "aac", "flac"];
const BITRATE_OPTIONS = [64, 96, 128, 192, 256, 320];
const HLS_FORMAT_OPTIONS = ["aac", "opus", "mp3"];

export const BroadcastingTab = ({ currentUser }) => {
  const [mounts, setMounts] = useState([]);
  const [hlsData, setHlsData] = useState({ hls_enabled: false, hls_url: "", streams: [] });
  const [loading, setLoading] = useState(true);
  const [showAddMount, setShowAddMount] = useState(false);
  const [showAddHls, setShowAddHls] = useState(false);
  const [msg, setMsg] = useState(null);
  const [copied, setCopied] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [mountsRes, hlsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/broadcasting/mounts`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${BACKEND_URL}/api/broadcasting/hls`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (mountsRes.ok) setMounts(await mountsRes.json());
      if (hlsRes.ok) setHlsData(await hlsRes.json());
    } catch (err) {
      console.warn("Broadcasting fetch error:", err.message);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const copyUrl = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  // ---- Mount CRUD ----
  const [mountForm, setMountForm] = useState({
    name: "/stream",
    display_name: "",
    is_visible_on_public_pages: true,
    is_default: false,
    enable_autodj: true,
    autodj_format: "mp3",
    autodj_bitrate: 128,
    max_listener_duration: 0,
  });

  const handleCreateMount = async () => {
    setActionLoading("create_mount");
    try {
      const res = await fetch(`${BACKEND_URL}/api/broadcasting/mounts`, {
        method: "POST", headers,
        body: JSON.stringify(mountForm),
      });
      if (res.ok) {
        flash("success", "Mount point created");
        setShowAddMount(false);
        setMountForm({ name: "/stream", display_name: "", is_visible_on_public_pages: true, is_default: false, enable_autodj: true, autodj_format: "mp3", autodj_bitrate: 128, max_listener_duration: 0 });
        fetchAll();
      } else {
        const data = await res.json().catch(() => ({}));
        flash("error", data.detail || "Failed to create mount point");
      }
    } catch {
      flash("error", "Failed to create mount point");
    }
    setActionLoading(null);
  };

  const handleDeleteMount = async (mountId) => {
    if (!window.confirm("Delete this mount point? Listeners will be disconnected.")) return;
    setActionLoading(`del_mount_${mountId}`);
    try {
      const res = await fetch(`${BACKEND_URL}/api/broadcasting/mounts/${mountId}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        flash("success", "Mount point deleted");
        fetchAll();
      } else {
        const data = await res.json().catch(() => ({}));
        flash("error", data.detail || "Failed to delete");
      }
    } catch {
      flash("error", "Failed to delete mount point");
    }
    setActionLoading(null);
  };

  // ---- HLS CRUD ----
  const [hlsForm, setHlsForm] = useState({ name: "aac_lofi", format: "aac", bitrate: 128 });

  const handleCreateHls = async () => {
    setActionLoading("create_hls");
    try {
      const res = await fetch(`${BACKEND_URL}/api/broadcasting/hls`, {
        method: "POST", headers,
        body: JSON.stringify(hlsForm),
      });
      if (res.ok) {
        flash("success", "HLS stream created");
        setShowAddHls(false);
        setHlsForm({ name: "aac_lofi", format: "aac", bitrate: 128 });
        fetchAll();
      } else {
        const data = await res.json().catch(() => ({}));
        flash("error", data.detail || "Failed to create HLS stream");
      }
    } catch {
      flash("error", "Failed to create HLS stream");
    }
    setActionLoading(null);
  };

  const handleDeleteHls = async (streamId) => {
    if (!window.confirm("Delete this HLS stream profile?")) return;
    setActionLoading(`del_hls_${streamId}`);
    try {
      const res = await fetch(`${BACKEND_URL}/api/broadcasting/hls/${streamId}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        flash("success", "HLS stream deleted");
        fetchAll();
      } else {
        const data = await res.json().catch(() => ({}));
        flash("error", data.detail || "Failed to delete");
      }
    } catch {
      flash("error", "Failed to delete HLS stream");
    }
    setActionLoading(null);
  };

  if (loading) return <div className="text-center text-white/40 py-16">Loading broadcasting config...</div>;

  return (
    <div className="space-y-6" data-testid="broadcasting-tab">
      {msg && (
        <div className={`px-4 py-3 rounded-xl text-sm font-medium ${msg.type === "success" ? "bg-green-500/10 text-green-400 border border-green-500/20" : "bg-red-500/10 text-red-400 border border-red-500/20"}`} data-testid="broadcasting-msg">
          {msg.text}
        </div>
      )}

      {/* ========== MOUNT POINTS ========== */}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="mount-points-section">
        <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center">
              <Radio className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Mount Points</h2>
              <p className="text-white/40 text-xs">Stream endpoints for your station</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchAll} className="text-white/40 hover:text-white p-2 rounded-lg hover:bg-white/5 transition" data-testid="refresh-mounts-btn">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={() => setShowAddMount(true)}
              className="flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-semibold transition"
              data-testid="add-mount-btn">
              <Plus className="w-4 h-4" /> Add Mount
            </button>
          </div>
        </div>

        {mounts.length === 0 ? (
          <div className="p-10 text-center text-white/30">
            <Radio className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No mount points configured yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {mounts.map((m) => (
              <div key={m.id} className="px-6 py-4 flex items-center justify-between hover:bg-white/[0.02] transition" data-testid={`mount-row-${m.id}`}>
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <Signal className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-semibold text-sm">{m.name || m.path}</p>
                      {m.is_default && (
                        <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full font-bold">DEFAULT</span>
                      )}
                      {m.format && (
                        <span className="text-[10px] bg-white/10 text-white/50 px-2 py-0.5 rounded-full uppercase font-medium">{m.format}</span>
                      )}
                      {m.bitrate && (
                        <span className="text-[10px] bg-white/10 text-white/50 px-2 py-0.5 rounded-full font-medium">{m.bitrate}kbps</span>
                      )}
                    </div>
                    {(m.display_name || m.name) && (
                      <p className="text-white/40 text-xs mt-0.5">{m.display_name || `${m.bitrate || '?'}kbps ${(m.format || m.autodj_format || 'mp3').toUpperCase()}`}</p>
                    )}
                    {m.url && (
                      <div className="flex items-center gap-2 mt-1">
                        <code className="text-xs text-cyan-400/80 bg-cyan-500/5 px-2 py-0.5 rounded truncate max-w-[300px]">{m.url}</code>
                        <button onClick={() => copyUrl(m.url, `mount_${m.id}`)} className="text-white/30 hover:text-white transition" data-testid={`copy-mount-url-${m.id}`}>
                          {copied === `mount_${m.id}` ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                  <div className="text-right">
                    <p className="text-white font-bold text-lg" data-testid={`mount-listeners-${m.id}`}>
                      {m.listeners?.total ?? m.listeners_total ?? 0}
                    </p>
                    <p className="text-white/30 text-[10px]">listeners</p>
                  </div>
                  <button onClick={() => handleDeleteMount(m.id)}
                    disabled={actionLoading === `del_mount_${m.id}` || m.is_default}
                    className="text-red-400/40 hover:text-red-400 disabled:opacity-20 disabled:cursor-not-allowed p-1.5 rounded-lg hover:bg-red-500/10 transition"
                    title={m.is_default ? "Cannot delete the default mount" : "Delete mount point"}
                    data-testid={`delete-mount-${m.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ========== HLS STREAMS ========== */}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="hls-section">
        <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/15 flex items-center justify-center">
              <Tv className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">HLS Streams</h2>
              <p className="text-white/40 text-xs">Adaptive bitrate streaming (M3U8)</p>
            </div>
            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-bold ${hlsData.hls_enabled ? "bg-green-500/20 text-green-400 border border-green-500/30" : "bg-white/10 text-white/40"}`} data-testid="hls-status">
              {hlsData.hls_enabled ? "Enabled" : "Disabled"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchAll} className="text-white/40 hover:text-white p-2 rounded-lg hover:bg-white/5 transition" data-testid="refresh-hls-btn">
              <RefreshCw className="w-4 h-4" />
            </button>
            {hlsData.hls_enabled && (
              <button onClick={() => setShowAddHls(true)}
                className="flex items-center gap-1.5 bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-xl text-sm font-semibold transition"
                data-testid="add-hls-btn">
                <Plus className="w-4 h-4" /> Add Stream
              </button>
            )}
          </div>
        </div>

        {hlsData.hls_enabled && hlsData.hls_url && (
          <div className="px-6 py-3 bg-purple-500/5 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-white/50 text-xs font-medium">HLS URL:</span>
              <code className="text-xs text-purple-300/80 bg-purple-500/10 px-2 py-0.5 rounded truncate max-w-[400px]">{hlsData.hls_url}</code>
              <button onClick={() => copyUrl(hlsData.hls_url, "hls_url")} className="text-white/30 hover:text-white transition" data-testid="copy-hls-url">
                {copied === "hls_url" ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-white/40 text-xs">{hlsData.hls_listeners || 0} listeners</span>
              <a href={hlsData.hls_url} target="_blank" rel="noopener noreferrer"
                className="text-purple-400 hover:text-purple-300 transition" data-testid="open-hls-url">
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        )}

        {!hlsData.hls_enabled ? (
          <div className="p-8 text-center">
            <Tv className="w-10 h-10 text-white/10 mx-auto mb-3" />
            <p className="text-white/40 text-sm mb-2">HLS streaming is not enabled on your station.</p>
            <div className="flex items-start gap-2 bg-amber-500/5 border border-amber-500/15 rounded-xl p-3 max-w-md mx-auto text-left">
              <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-amber-300/70 text-xs">Enable HLS in your AzuraCast station profile under the AutoDJ settings to use adaptive bitrate streaming.</p>
            </div>
          </div>
        ) : hlsData.streams.length === 0 ? (
          <div className="p-8 text-center text-white/30">
            <Tv className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No HLS stream profiles configured. Add one to customize bitrate options.</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {hlsData.streams.map((s) => (
              <div key={s.id} className="px-6 py-4 flex items-center justify-between hover:bg-white/[0.02] transition" data-testid={`hls-row-${s.id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                    <Tv className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{s.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {s.format && (
                        <span className="text-[10px] bg-purple-500/15 text-purple-300 px-2 py-0.5 rounded-full uppercase font-medium">{s.format}</span>
                      )}
                      {s.bitrate && (
                        <span className="text-[10px] bg-white/10 text-white/50 px-2 py-0.5 rounded-full font-medium">{s.bitrate}kbps</span>
                      )}
                    </div>
                  </div>
                </div>
                <button onClick={() => handleDeleteHls(s.id)}
                  disabled={actionLoading === `del_hls_${s.id}`}
                  className="text-red-400/40 hover:text-red-400 p-1.5 rounded-lg hover:bg-red-500/10 transition disabled:opacity-20"
                  data-testid={`delete-hls-${s.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ========== ADD MOUNT MODAL ========== */}
      {showAddMount && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="add-mount-modal-overlay">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 max-w-lg w-full" data-testid="add-mount-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-white">New Mount Point</h2>
              <button onClick={() => setShowAddMount(false)} className="text-white/60 hover:text-white p-2 rounded-lg hover:bg-white/5">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1.5">Mount Path <span className="text-red-400">*</span></label>
                <input type="text" value={mountForm.name} onChange={e => setMountForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500 transition"
                  placeholder="/radio.mp3" data-testid="mount-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1.5">Display Name</label>
                <input type="text" value={mountForm.display_name} onChange={e => setMountForm(f => ({ ...f, display_name: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500 transition"
                  placeholder="128kbps MP3" data-testid="mount-display-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1.5">Format</label>
                  <select value={mountForm.autodj_format} onChange={e => setMountForm(f => ({ ...f, autodj_format: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500 transition appearance-none"
                    data-testid="mount-format-select">
                    {FORMAT_OPTIONS.map(f => <option key={f} value={f} className="bg-gray-900">{f.toUpperCase()}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1.5">Bitrate</label>
                  <select value={mountForm.autodj_bitrate} onChange={e => setMountForm(f => ({ ...f, autodj_bitrate: parseInt(e.target.value) }))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500 transition appearance-none"
                    data-testid="mount-bitrate-select">
                    {BITRATE_OPTIONS.map(b => <option key={b} value={b} className="bg-gray-900">{b}kbps</option>)}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={mountForm.is_visible_on_public_pages}
                    onChange={e => setMountForm(f => ({ ...f, is_visible_on_public_pages: e.target.checked }))}
                    className="w-4 h-4 rounded bg-white/5 border-white/20 text-blue-500 focus:ring-blue-500" />
                  <span className="text-sm text-white/70">Public</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={mountForm.enable_autodj}
                    onChange={e => setMountForm(f => ({ ...f, enable_autodj: e.target.checked }))}
                    className="w-4 h-4 rounded bg-white/5 border-white/20 text-blue-500 focus:ring-blue-500" />
                  <span className="text-sm text-white/70">Enable AutoDJ</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={mountForm.is_default}
                    onChange={e => setMountForm(f => ({ ...f, is_default: e.target.checked }))}
                    className="w-4 h-4 rounded bg-white/5 border-white/20 text-blue-500 focus:ring-blue-500" />
                  <span className="text-sm text-white/70">Default</span>
                </label>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button onClick={() => setShowAddMount(false)}
                className="bg-white/10 hover:bg-white/20 text-white px-5 py-2.5 rounded-xl transition text-sm font-medium">Cancel</button>
              <button onClick={handleCreateMount}
                disabled={actionLoading === "create_mount" || !mountForm.name.trim()}
                className="bg-blue-500 hover:bg-blue-600 disabled:opacity-40 text-white px-5 py-2.5 rounded-xl transition text-sm font-semibold"
                data-testid="create-mount-submit">
                {actionLoading === "create_mount" ? "Creating..." : "Create Mount Point"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== ADD HLS MODAL ========== */}
      {showAddHls && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="add-hls-modal-overlay">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 max-w-lg w-full" data-testid="add-hls-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-white">New HLS Stream Profile</h2>
              <button onClick={() => setShowAddHls(false)} className="text-white/60 hover:text-white p-2 rounded-lg hover:bg-white/5">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1.5">Stream Name <span className="text-red-400">*</span></label>
                <input type="text" value={hlsForm.name} onChange={e => setHlsForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-purple-500 transition"
                  placeholder="aac_hifi" data-testid="hls-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1.5">Format</label>
                  <select value={hlsForm.format} onChange={e => setHlsForm(f => ({ ...f, format: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-purple-500 transition appearance-none"
                    data-testid="hls-format-select">
                    {HLS_FORMAT_OPTIONS.map(f => <option key={f} value={f} className="bg-gray-900">{f.toUpperCase()}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1.5">Bitrate</label>
                  <select value={hlsForm.bitrate} onChange={e => setHlsForm(f => ({ ...f, bitrate: parseInt(e.target.value) }))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-purple-500 transition appearance-none"
                    data-testid="hls-bitrate-select">
                    {BITRATE_OPTIONS.map(b => <option key={b} value={b} className="bg-gray-900">{b}kbps</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button onClick={() => setShowAddHls(false)}
                className="bg-white/10 hover:bg-white/20 text-white px-5 py-2.5 rounded-xl transition text-sm font-medium">Cancel</button>
              <button onClick={handleCreateHls}
                disabled={actionLoading === "create_hls" || !hlsForm.name.trim()}
                className="bg-purple-500 hover:bg-purple-600 disabled:opacity-40 text-white px-5 py-2.5 rounded-xl transition text-sm font-semibold"
                data-testid="create-hls-submit">
                {actionLoading === "create_hls" ? "Creating..." : "Create HLS Stream"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
